import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../cn';
import { useTimezone } from '../timezone/TimezoneContext';
import { DataTableShell, SortableTableHead, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../data-table';
import { CopyTextButton } from '../portal-ledger/CopyTextButton';
import { ViewTransactionButton } from '../portal-ledger/ViewTransactionButton';
import { useTerms } from '../terms/TermsContext';
import { creditLedgerCreatedAt, creditLedgerDisplayStatus, creditLedgerSignedAmount, creditLedgerStatusBadgeClass, creditLedgerStatusLabel, creditLedgerTypeLabel, creditLedgerActivityLabel, creditPartyDisplayLabel, formatCreditLedgerAmount, partyLabel, } from './ledgerUtils';
const COL_WIDTHS = ['11%', '15%', '12%', '12%', '12%', '8%', '8%', '9%', '9%', '4%'];
const TABLE_HEAD_BASE = 'h-10 px-4 text-xs font-semibold uppercase tracking-wide text-gray-500';
const TABLE_HEAD_LEFT = cn(TABLE_HEAD_BASE, 'text-left');
const TABLE_HEAD_RIGHT = cn(TABLE_HEAD_BASE, 'text-right');
function ledgerSortDirection(sortDir) {
    return sortDir === 'asc' ? 'ASC' : 'DESC';
}
function AdminSortableHead({ label, field, sort, sortDir, onSort, className, align = 'left', magnitude = false, }) {
    const direction = sort === field ? ledgerSortDirection(sortDir) : null;
    return (React.createElement(SortableTableHead, { sortable: true, sortKey: field, label: label, activeKey: sort, direction: direction, onSort: () => onSort(field), align: align, magnitude: magnitude, className: className }));
}
function SortableHead({ label, field, sort, sortDir, onSort, className, align = 'left', }) {
    return (React.createElement(TableHead, { className: cn(align === 'right' ? 'text-right' : 'text-left', className) },
        React.createElement("button", { type: "button", onClick: () => onSort(field), className: "inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-800" },
            label,
            React.createElement("span", { className: "text-[10px] text-gray-400", "aria-hidden": true }, sort === field ? (sortDir === 'asc' ? '↑' : '↓') : '↕'))));
}
function FraudWarningIcon({ row, onOpenFraudIndicator, }) {
    var _a;
    const fraudId = (_a = row.fraudFlag) === null || _a === void 0 ? void 0 : _a.id;
    const clickable = Boolean(fraudId && onOpenFraudIndicator);
    const icon = (React.createElement(AlertTriangle, { className: cn('h-3.5 w-3.5 shrink-0 text-amber-600', clickable && 'cursor-pointer hover:text-amber-700'), "aria-hidden": true }));
    if (clickable) {
        return (React.createElement("button", { type: "button", onClick: () => onOpenFraudIndicator(fraudId), className: "inline-flex shrink-0 rounded p-0.5 hover:bg-amber-50", "aria-label": "Flagged by fraud indicator", title: "Flagged by fraud indicator" }, icon));
    }
    return (React.createElement("span", { title: "Flagged by fraud indicator", "aria-label": "Flagged by fraud indicator" }, icon));
}
function truncateMonoId(txId, fallbackId) {
    const full = txId || fallbackId || '—';
    const display = full.length > 12 && txId ? `${txId.slice(0, 8)}…` : full;
    return { display, full };
}
function partyAdminPath(ownerType, ownerId) {
    const type = String(ownerType !== null && ownerType !== void 0 ? ownerType : '').trim().toLowerCase();
    const id = String(ownerId !== null && ownerId !== void 0 ? ownerId : '').trim();
    if (!id)
        return null;
    if (type === 'admin')
        return '/admins';
    if (type === 'agent')
        return `/agents/${encodeURIComponent(id)}`;
    if (type === 'super_agent')
        return `/super-agents/${encodeURIComponent(id)}`;
    if (type === 'shop')
        return `/shops/${encodeURIComponent(id)}`;
    if (type === 'player')
        return `/users/${encodeURIComponent(id)}`;
    return null;
}
const partyTextClass = 'block w-full truncate whitespace-nowrap text-sm font-medium leading-snug text-indigo-700';
function PartyLink({ label, href }) {
    const isEmpty = label === '—';
    if (!href) {
        return (React.createElement("span", { className: cn('block w-full truncate whitespace-nowrap text-sm leading-snug', isEmpty ? 'text-gray-900' : partyTextClass), title: label }, label));
    }
    return (React.createElement("a", { href: href, className: cn(partyTextClass, 'hover:text-indigo-900 hover:underline'), title: label }, label));
}
function resolveMissingPartyLabel(row, side, terms) {
    var _a, _b;
    const signed = creditLedgerSignedAmount(row);
    const ledgerType = String((_a = row.ledgerType) !== null && _a !== void 0 ? _a : '').toLowerCase();
    const type = String((_b = row.type) !== null && _b !== void 0 ? _b : '').toLowerCase();
    const isDeposit = ledgerType === 'deposit' || type === 'credit' || signed > 1e-9;
    const isWithdraw = ledgerType === 'withdraw' || type === 'debit' || signed < -1e-9;
    // Game provider sits on the missing counterparty side of game flows.
    if (side === 'from' && isDeposit)
        return creditPartyDisplayLabel(null, row, terms);
    if (side === 'to' && isWithdraw)
        return creditPartyDisplayLabel(null, row, terms);
    return '—';
}
function FromPartyBlock({ row }) {
    var _a;
    const t = useTerms();
    const fromParty = (_a = row.fromParty) !== null && _a !== void 0 ? _a : null;
    const from = fromParty ? partyLabel(fromParty, t) : resolveMissingPartyLabel(row, 'from', t);
    const fromHref = partyAdminPath(fromParty === null || fromParty === void 0 ? void 0 : fromParty.ownerType, fromParty === null || fromParty === void 0 ? void 0 : fromParty.ownerId);
    return React.createElement(PartyLink, { label: from, href: fromHref });
}
function ToPartyBlock({ row }) {
    var _a;
    const t = useTerms();
    const toParty = (_a = row.toParty) !== null && _a !== void 0 ? _a : null;
    const to = toParty ? partyLabel(toParty, t) : resolveMissingPartyLabel(row, 'to', t);
    const toHref = partyAdminPath(toParty === null || toParty === void 0 ? void 0 : toParty.ownerType, toParty === null || toParty === void 0 ? void 0 : toParty.ownerId);
    return React.createElement(PartyLink, { label: to, href: toHref });
}
function OwnerColumnBlock({ row }) {
    const t = useTerms();
    const label = partyLabel(row.ownerParty, t);
    return (React.createElement("p", { className: "truncate whitespace-nowrap text-sm leading-snug text-gray-900", title: label }, label));
}
function ActivityBlock({ row }) {
    const t = useTerms();
    const label = creditLedgerActivityLabel(row, t);
    return (React.createElement("p", { className: "text-sm leading-snug text-gray-800", title: label }, label));
}
function TransactionIdBlock({ row }) {
    var _a, _b;
    const txId = String((_b = (_a = row.transactionId) !== null && _a !== void 0 ? _a : row.transaction_id) !== null && _b !== void 0 ? _b : '').trim();
    if (!txId)
        return React.createElement("span", { className: "text-gray-500" }, "\u2014");
    return (React.createElement("div", { className: "flex min-w-0 items-center gap-1" },
        React.createElement("span", { className: "min-w-0 truncate font-mono text-xs text-gray-800", title: txId }, txId.length > 18 ? `${txId.slice(0, 14)}…` : txId),
        React.createElement(CopyTextButton, { text: txId, label: "Copy transaction ID" })));
}
function StatusBlock({ row }) {
    const t = useTerms();
    const typeLabel = creditLedgerTypeLabel(row);
    const isDeposit = typeLabel === 'Deposit';
    const isWithdraw = typeLabel === 'Withdraw';
    const status = creditLedgerDisplayStatus(row);
    const statusLabel = creditLedgerStatusLabel(status);
    return (React.createElement("div", { className: "flex min-w-0 flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center" },
        React.createElement("span", { className: cn('inline-flex w-fit shrink-0 rounded-md px-2 py-0.5 text-xs font-semibold', isDeposit && 'bg-emerald-100 text-emerald-800', isWithdraw && 'bg-red-100 text-red-800', !isDeposit && !isWithdraw && 'bg-gray-100 text-gray-700') }, t.credit.typeDisplay(typeLabel)),
        React.createElement("span", { className: cn('inline-flex w-fit shrink-0 rounded-md px-2 py-0.5 text-xs font-semibold capitalize', creditLedgerStatusBadgeClass(status)) }, statusLabel)));
}
function AmountBlock({ row }) {
    const signed = creditLedgerSignedAmount(row);
    return (React.createElement("span", { className: cn('block text-right font-semibold tabular-nums', signed > 1e-9 ? 'text-emerald-700' : signed < -1e-9 ? 'text-red-700' : 'text-gray-700') }, formatCreditLedgerAmount(row)));
}
function TypeBadgeColumnBlock({ row }) {
    const t = useTerms();
    const typeLabel = creditLedgerTypeLabel(row);
    const isDeposit = typeLabel === 'Deposit';
    const isWithdraw = typeLabel === 'Withdraw';
    return (React.createElement("span", { className: cn('inline-flex w-fit rounded-md px-2 py-0.5 text-xs font-semibold', isDeposit && 'bg-emerald-100 text-emerald-800', isWithdraw && 'bg-red-100 text-red-800', !isDeposit && !isWithdraw && 'bg-gray-100 text-gray-700') }, t.credit.typeDisplay(typeLabel)));
}
function StatusBadgeColumnBlock({ row }) {
    const status = creditLedgerDisplayStatus(row);
    return (React.createElement("span", { className: cn('inline-flex w-fit rounded-md px-2 py-0.5 text-xs font-semibold capitalize', creditLedgerStatusBadgeClass(status)) }, creditLedgerStatusLabel(status)));
}
function CreatedBlock({ row, timezone }) {
    return (React.createElement("span", { className: "whitespace-nowrap text-right text-xs tabular-nums text-gray-600" }, creditLedgerCreatedAt(row, timezone)));
}
function MobileCreditLedgerCard({ row, onViewDetail, onOpenFraudIndicator, timezone }) {
    var _a, _b, _c, _d;
    const txId = String((_b = (_a = row.transactionId) !== null && _a !== void 0 ? _a : row.transaction_id) !== null && _b !== void 0 ? _b : '').trim();
    const ledgerId = String((_c = row.id) !== null && _c !== void 0 ? _c : '').trim();
    const { display, full } = truncateMonoId(txId, ledgerId);
    const copyText = txId || ledgerId;
    const fraudFlagged = Boolean((_d = row.fraudFlagged) !== null && _d !== void 0 ? _d : row.fraudFlag);
    return (React.createElement("article", { className: "space-y-3 border-b border-gray-100 px-4 py-4 last:border-b-0" },
        React.createElement("div", { className: "flex items-start justify-between gap-3" },
            React.createElement("div", { className: "min-w-0 flex-1" },
                React.createElement("div", { className: "flex min-w-0 items-center gap-1" },
                    fraudFlagged ? React.createElement(FraudWarningIcon, { row: row, onOpenFraudIndicator: onOpenFraudIndicator }) : null,
                    React.createElement("span", { className: "min-w-0 truncate font-mono text-xs font-medium text-gray-800", title: full !== '—' ? full : undefined }, display),
                    copyText ? React.createElement(CopyTextButton, { text: copyText, label: "Copy transaction ID" }) : null)),
            onViewDetail ? (React.createElement("div", { className: "shrink-0" },
                React.createElement(ViewTransactionButton, { onClick: () => onViewDetail(row) }))) : null),
        React.createElement("div", { className: "grid grid-cols-2 gap-3" },
            React.createElement("div", null,
                React.createElement("p", { className: "text-[11px] font-medium uppercase tracking-wide text-gray-500" }, "Activity"),
                React.createElement(ActivityBlock, { row: row })),
            React.createElement("div", null,
                React.createElement("p", { className: "text-[11px] font-medium uppercase tracking-wide text-gray-500" }, "Owner"),
                React.createElement(OwnerColumnBlock, { row: row }))),
        React.createElement("div", { className: "grid grid-cols-2 gap-3" },
            React.createElement(FromPartyBlock, { row: row }),
            React.createElement(ToPartyBlock, { row: row })),
        React.createElement("div", { className: "flex flex-wrap items-center justify-between gap-3" },
            React.createElement(StatusBlock, { row: row }),
            React.createElement("div", { className: "text-right" },
                React.createElement("p", { className: "text-[11px] font-medium uppercase tracking-wide text-gray-500" }, "Amount"),
                React.createElement(AmountBlock, { row: row }))),
        React.createElement("div", { className: "flex items-end justify-end gap-4" },
            React.createElement("div", { className: "text-right" },
                React.createElement("p", { className: "text-[11px] font-medium uppercase tracking-wide text-gray-500" }, "Created"),
                React.createElement(CreatedBlock, { row: row, timezone: timezone })))));
}
export const PortalCreditLedgerTable = ({ rows, loading, isGloballyEmpty, sort, sortDir, onSort, onViewDetail, onOpenFraudIndicator, className, sortable = false, }) => {
    const t = useTerms();
    const { timezone } = useTimezone();
    const isRefetching = Boolean(loading && rows.length > 0);
    if (loading && rows.length === 0) {
        return (React.createElement("div", { className: cn('px-4 py-12 text-center text-sm text-gray-500', className) }, "Loading credit transactions\u2026"));
    }
    if (!loading && rows.length === 0) {
        return (React.createElement("div", { className: cn('px-4 py-14 text-center', className) },
            React.createElement("p", { className: "text-sm font-medium text-gray-900" }, "No transactions found"),
            React.createElement("p", { className: "mt-1 text-sm text-gray-500" }, isGloballyEmpty
                ? 'Credit activity will appear here once transactions occur.'
                : t.credit.emptyFilteredHint)));
    }
    return (React.createElement(DataTableShell, { borderless: true, className: className },
        React.createElement("div", { className: cn('md:hidden', isRefetching && 'opacity-60 transition-opacity'), "aria-busy": isRefetching || undefined }, rows.map((r) => (React.createElement(MobileCreditLedgerCard, { key: String(r.id), row: r, onViewDetail: onViewDetail, onOpenFraudIndicator: onOpenFraudIndicator, timezone: timezone })))),
        React.createElement("div", { className: cn('hidden md:block', isRefetching && 'opacity-60 transition-opacity') },
            React.createElement(Table, { className: "table-fixed w-full", "aria-busy": isRefetching || undefined },
                React.createElement("colgroup", null, COL_WIDTHS.map((w, index) => (React.createElement("col", { key: `${w}-${index}`, style: { width: w } })))),
                React.createElement(TableHeader, null,
                    React.createElement(TableRow, { className: "border-b border-gray-100 bg-gray-50/90 hover:bg-gray-50/90" },
                        React.createElement(TableHead, { className: TABLE_HEAD_LEFT }, "Transaction ID"),
                        React.createElement(TableHead, { className: TABLE_HEAD_LEFT }, "Activity"),
                        React.createElement(TableHead, { className: TABLE_HEAD_LEFT }, "Owner"),
                        sortable ? (React.createElement(React.Fragment, null,
                            React.createElement(AdminSortableHead, { label: "From", field: "from", sort: sort, sortDir: sortDir, onSort: onSort, className: "h-10 px-4" }),
                            React.createElement(AdminSortableHead, { label: "To", field: "to", sort: sort, sortDir: sortDir, onSort: onSort, className: "h-10 px-4" }))) : (React.createElement(React.Fragment, null,
                            React.createElement(TableHead, { className: TABLE_HEAD_LEFT }, "From"),
                            React.createElement(TableHead, { className: TABLE_HEAD_LEFT }, "To"))),
                        React.createElement(TableHead, { className: TABLE_HEAD_LEFT }, "Type"),
                        React.createElement(TableHead, { className: TABLE_HEAD_LEFT }, "Status"),
                        sortable ? (React.createElement(AdminSortableHead, { label: "Amount", field: "amount", sort: sort, sortDir: sortDir, onSort: onSort, className: "h-10 px-4 text-right", align: "right", magnitude: true })) : (React.createElement(TableHead, { className: TABLE_HEAD_RIGHT }, "Amount")),
                        sortable ? (React.createElement(AdminSortableHead, { label: "Created", field: "created", sort: sort, sortDir: sortDir, onSort: onSort, className: "h-10 px-4 text-right", align: "right" })) : (React.createElement(SortableHead, { label: "Created", field: "created", sort: sort, sortDir: sortDir, onSort: onSort, className: "h-10 px-4 text-right", align: "right" })),
                        React.createElement(TableHead, { className: "h-10 px-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500" }, "Action"))),
                React.createElement(TableBody, null, rows.map((r) => (React.createElement(TableRow, { key: String(r.id), className: "border-gray-100 hover:bg-gray-50/50" },
                    React.createElement(TableCell, { className: "min-w-0 px-4 py-3 align-top" },
                        React.createElement(TransactionIdBlock, { row: r })),
                    React.createElement(TableCell, { className: "min-w-0 px-4 py-3 align-top" },
                        React.createElement(ActivityBlock, { row: r })),
                    React.createElement(TableCell, { className: "min-w-0 px-4 py-3 align-top" },
                        React.createElement(OwnerColumnBlock, { row: r })),
                    React.createElement(TableCell, { className: "min-w-0 px-4 py-3 align-top" },
                        React.createElement(FromPartyBlock, { row: r })),
                    React.createElement(TableCell, { className: "min-w-0 px-4 py-3 align-top" },
                        React.createElement(ToPartyBlock, { row: r })),
                    React.createElement(TableCell, { className: "px-4 py-3 align-top" },
                        React.createElement(TypeBadgeColumnBlock, { row: r })),
                    React.createElement(TableCell, { className: "px-4 py-3 align-top" },
                        React.createElement(StatusBadgeColumnBlock, { row: r })),
                    React.createElement(TableCell, { className: "px-4 py-3 align-top" },
                        React.createElement(AmountBlock, { row: r })),
                    React.createElement(TableCell, { className: "px-4 py-3 align-top text-right" },
                        React.createElement(CreatedBlock, { row: r, timezone: timezone })),
                    React.createElement(TableCell, { className: "px-2 py-3 text-right align-top" }, onViewDetail ? React.createElement(ViewTransactionButton, { onClick: () => onViewDetail(r) }) : null)))))))));
};
export default PortalCreditLedgerTable;
